import { addScript, rejectAfter } from '@tunein/web-utils';
import { EventEmitter } from 'eventemitter3';
import { BLANK_MP3_DATA_URL } from '../../../constants';
import { ErrorCode, ErrorName, ErrorType, PlayerError } from '../../../errors';
import waitForGlobal from '../../../utils/waitForGlobal';
import { debug } from './debug';
const SCRIPT_SRC = '//imasdk.googleapis.com/js/sdkloader/ima3.js';
const DEBUG_SCRIPT_SRC = '//imasdk.googleapis.com/js/sdkloader/ima3_debug.js';
const DEFAULT_VIDEO_WIDTH = 640;
const DEFAULT_VIDEO_HEIGHT = 360;
const ADS_LOAD_TIMEOUT = 5000;
const DEFAULT_PROVIDER_OPTIONS = {
    mediaElementId: 'web-media-player-ad-player',
    containerId: 'web-media-player-ad-container',
};
export class GoogleIMAProvider extends EventEmitter {
    static #readyPromise = Promise.withResolvers();
    static #initStarted = false;
    static get ready() {
        return GoogleIMAProvider.#readyPromise.promise;
    }
    static async init() {
        if (GoogleIMAProvider.#initStarted) {
            return GoogleIMAProvider.#readyPromise.promise;
        }
        GoogleIMAProvider.#initStarted = true;
        const useDebugSdk = debug.isVerboseActive();
        debug('Initializing Google IMA SDK', {
            useDebugSdk,
        });
        try {
            await Promise.all([
                addScript({ src: useDebugSdk ? DEBUG_SCRIPT_SRC : SCRIPT_SRC }),
                waitForGlobal('google'),
            ]);
            GoogleIMAProvider.#readyPromise.resolve();
        }
        catch {
            GoogleIMAProvider.#readyPromise.reject(new Error('Google IMA SDK loading failed'));
        }
        return GoogleIMAProvider.#readyPromise.promise;
    }
    #options;
    #isReady = false;
    #isInitializing = false;
    #adsLoaded = false;
    #isPaused = false;
    #adDisplayContainer;
    #adsLoader;
    #adsManager;
    #adManagerEvents;
    #mediaElement;
    #containerElement;
    #adRequestId;
    #duration;
    #currentTime;
    #didAdLoad = false;
    #didAdFail = false;
    get name() {
        return 'google';
    }
    get isReady() {
        return this.#isReady;
    }
    get isInitializing() {
        return this.#isInitializing;
    }
    get volume() {
        return this.#adsManager?.getVolume() || 0;
    }
    get duration() {
        return this.#duration || 0;
    }
    get currentTime() {
        return this.#currentTime || 0;
    }
    get mediaElement() {
        return this.#mediaElement;
    }
    get containerElement() {
        return this.#containerElement;
    }
    constructor(options) {
        super();
        this.#options = {
            ...DEFAULT_PROVIDER_OPTIONS,
            ...options,
        };
    }
    async playBlankMp3() {
        const { mediaElement } = this.#getAdElements();
        debug.verbose('Attempting Google IMA blank MP3 autoplay prime', {
            containerId: this.#options.containerId,
            mediaElementId: this.#options.mediaElementId,
        });
        mediaElement.src = BLANK_MP3_DATA_URL;
        try {
            await mediaElement.play();
            debug.verbose('Google IMA blank MP3 autoplay prime succeeded');
            return true;
        }
        catch (error) {
            debug.verbose('Google IMA blank MP3 autoplay prime failed', {
                containerId: this.#options.containerId,
                mediaElementId: this.#options.mediaElementId,
                message: error instanceof Error ? error.message : String(error),
                name: error?.name,
            });
            return false;
        }
    }
    async load(adRequestId, adRequestUrl) {
        this.#adRequestId = adRequestId;
        this.#didAdLoad = false;
        this.#didAdFail = false;
        const { promise, resolve } = Promise.withResolvers();
        const onReady = () => {
            this.#isReady = true;
            this.#isInitializing = false;
            this.off('aderror', onError);
            resolve();
        };
        const onError = () => {
            this.off('ready', onReady);
            resolve();
        };
        this.once('ready', onReady);
        this.once('aderror', onError);
        this.#applyImaSettings();
        this.#initAdsLoader();
        const adsRequest = this.#createAdsRequest(adRequestUrl);
        this.#adsLoader?.requestAds(adsRequest);
        await rejectAfter(promise, this.#options.adLoaderTimeout || ADS_LOAD_TIMEOUT, new PlayerError('Google IMA SDK ads loader timeout', ErrorCode.RequestTimeout, ErrorType.LOAD_ERROR, ErrorName.AdError, false));
    }
    setVolume(volume) {
        this.#adsManager?.setVolume(volume);
    }
    pause() {
        debug('Ad pause requested');
        if (this.#adsLoaded) {
            this.#isPaused = true;
            this.#adsManager?.pause();
        }
    }
    skip() {
        this.#adsManager?.skip();
    }
    play() {
        debug('Ad play requested', {
            isPaused: this.#isPaused,
            adsLoaded: this.#adsLoaded,
        });
        if (this.#isPaused) {
            this.#adsManager?.resume();
            this.#isPaused = false;
            return;
        }
        if (this.#adsLoaded) {
            return;
        }
        this.#adsLoaded = true;
        this.#adDisplayContainer?.initialize();
        try {
            const initWidth = this.#containerElement?.clientWidth || DEFAULT_VIDEO_WIDTH;
            const initHeight = this.#containerElement?.clientHeight || DEFAULT_VIDEO_HEIGHT;
            this.#adsManager?.init(initWidth, initHeight, google.ima.ViewMode.NORMAL);
            debug('Ad start requested');
            this.#adsManager?.start();
        }
        catch (error) {
            debug('Ad start error', error);
            this.#adsLoaded = false;
            this.#isPaused = false;
            this.#destroyAdsManager();
            this.#emitAdError({
                errorCode: 0,
                innerError: error instanceof Error ? error : null,
                message: error instanceof Error ? error.message : 'Google IMA ad start failed',
                name: error instanceof Error ? error.name : 'AdPlayError',
                shouldAbortAdBreak: true,
                type: 'playError',
                vastErrorCode: 0,
            });
        }
    }
    resizeVideo(width, height) {
        try {
            if (!this.#containerElement?.clientHeight) {
                this.#containerElement.style.height = `${height}px`;
            }
            this.#adsManager?.resize(width, height, google.ima.ViewMode.NORMAL);
        }
        catch {
        }
    }
    forceSkipAd() {
        debug('Force skipping the ad');
        this.#adsManager?.stop();
        this.emit('adplaybackcancelled');
    }
    destroy() {
        this.#adsLoaded = false;
        this.#isPaused = false;
        this.#isReady = false;
        this.#isInitializing = false;
        this.#adRequestId = undefined;
        this.#didAdLoad = false;
        this.#didAdFail = false;
        this.#destroyAdsManager();
        this.#adsLoader?.destroy();
        this.#adDisplayContainer?.destroy();
        this.#adsLoader = undefined;
        this.#adDisplayContainer = undefined;
        this.#containerElement?.removeAttribute('data-video');
        this.#mediaElement?.remove();
    }
    #destroyAdsManager() {
        this.#deregisterAdsManagerListeners();
        this.#adsManager?.stop();
        this.#adsManager?.destroy();
        this.#adsManager = undefined;
        this.#adManagerEvents = undefined;
    }
    #initAdsLoader() {
        this.#isInitializing = true;
        if (!this.#options.containerId) {
            throw new Error('Missing containerId');
        }
        const { containerElement, mediaElement } = this.#getAdElements();
        this.#mediaElement = mediaElement;
        this.#containerElement = containerElement;
        this.#adDisplayContainer = new google.ima.AdDisplayContainer(this.#containerElement, this.#mediaElement);
        this.#createAdsLoader(this.#adDisplayContainer);
    }
    #applyImaSettings() {
        const { ppid, vpaidMode } = this.#options;
        if (ppid) {
            google.ima.settings.setPpid(ppid);
        }
        if (vpaidMode) {
            const VPAID_MODES = {
                disabled: google.ima.ImaSdkSettings.VpaidMode.DISABLED,
                enabled: google.ima.ImaSdkSettings.VpaidMode.ENABLED,
                insecure: google.ima.ImaSdkSettings.VpaidMode.INSECURE,
            };
            google.ima.settings.setVpaidMode(VPAID_MODES[vpaidMode]);
        }
    }
    #getAdElements() {
        const containerId = this.#options.containerId;
        const mediaElementId = this.#options.mediaElementId;
        let containerElement = document.getElementById(containerId);
        if (!containerElement) {
            containerElement = document.createElement('div');
            containerElement.id = containerId;
            document.body.appendChild(containerElement);
        }
        let mediaElement = document.getElementById(mediaElementId);
        if (!mediaElement) {
            mediaElement = document.createElement('video');
            mediaElement.id = mediaElementId;
            mediaElement.setAttribute('playsinline', '');
        }
        if (!containerElement.contains(mediaElement)) {
            containerElement.appendChild(mediaElement);
        }
        return { containerElement, mediaElement };
    }
    #createAdsLoader(adDisplayContainer) {
        if (!this.#adsLoader) {
            this.#adsLoader = new google.ima.AdsLoader(adDisplayContainer);
        }
        this.#registerAdsLoaderListeners();
    }
    #registerAdsLoaderListeners = () => {
        this.#adsLoader?.addEventListener(google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, this.#onAdsManagerLoaded, false);
        this.#adsLoader?.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, this.#onAdError(), false);
    };
    #createAdsRequest(adRequestUrl) {
        const adsRequest = new google.ima.AdsRequest();
        const mediaWidth = this.#mediaElement?.clientWidth || 0;
        const mediaHeight = this.#mediaElement?.clientHeight || 0;
        const containerWidth = this.#containerElement?.clientWidth || 0;
        const containerHeight = this.#containerElement?.clientHeight || 0;
        const slotWidth = containerWidth || mediaWidth || DEFAULT_VIDEO_WIDTH;
        const slotHeight = containerHeight || mediaHeight || DEFAULT_VIDEO_HEIGHT;
        adsRequest.adTagUrl = adRequestUrl;
        adsRequest.setAdWillAutoPlay(true);
        adsRequest.setAdWillPlayMuted(Boolean(this.#options.adWillPlayMuted));
        adsRequest.linearAdSlotWidth = slotWidth;
        adsRequest.linearAdSlotHeight = slotHeight;
        adsRequest.nonLinearAdSlotWidth = slotWidth;
        adsRequest.nonLinearAdSlotHeight = slotHeight;
        debug('Creating Google IMA ads request', {
            adWillPlayMuted: Boolean(this.#options.adWillPlayMuted),
            containerWidth,
            containerHeight,
            mediaWidth,
            mediaHeight,
            slotWidth,
            slotHeight,
        });
        return adsRequest;
    }
    #onAdsManagerLoaded = (event) => {
        if (!this.#mediaElement) {
            return;
        }
        this.#adsManager = event.getAdsManager(this.#mediaElement);
        this.#registerAdsManagerListeners();
        this.emit('ready');
    };
    #getCompanionAds = (ad) => {
        try {
            if (!ad) {
                return [];
            }
            const companionAds = this.#getCompanionAdsBySize(ad);
            return companionAds.map((companionAd) => ({
                height: companionAd.getHeight(),
                width: companionAd.getWidth(),
                content: companionAd.getContent(),
            }));
        }
        catch {
            return [];
        }
    };
    #getCompanionAdsBySize(ad) {
        if (!this.#options.companionAdSizes) {
            return [];
        }
        const selectionCriteria = new google.ima.CompanionAdSelectionSettings();
        selectionCriteria.resourceType = google.ima.CompanionAdSelectionSettings.ResourceType.ALL;
        selectionCriteria.creativeType = google.ima.CompanionAdSelectionSettings.CreativeType.ALL;
        selectionCriteria.sizeCriteria =
            google.ima.CompanionAdSelectionSettings.SizeCriteria.SELECT_EXACT_MATCH;
        let companionAds = [];
        this.#options.companionAdSizes.forEach(({ width, height }) => {
            const companions = ad.getCompanionAds(width, height, selectionCriteria);
            if (companions?.length) {
                companionAds = companionAds.concat(companions);
            }
        });
        return companionAds;
    }
    #registerAdsManagerListeners() {
        this.#adManagerEvents = {
            [google.ima.AdEvent.Type.AD_BUFFERING]: this.#onAdEvent('adbuffering'),
            [google.ima.AdErrorEvent.Type.AD_ERROR]: this.#onAdError(),
            [google.ima.AdEvent.Type.AD_METADATA]: this.#onAdEvent('admetadata'),
            [google.ima.AdEvent.Type.AD_PROGRESS]: this.#onAdProgress,
            [google.ima.AdEvent.Type.ALL_ADS_COMPLETED]: this.#onAllAdsCompleted,
            [google.ima.AdEvent.Type.CLICK]: this.#onAdEvent('adclick'),
            [google.ima.AdEvent.Type.COMPLETE]: this.#onAdEvent('adcomplete'),
            [google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED]: this.#onContentPauseRequested,
            [google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED]: this.#onContentResumeRequested,
            [google.ima.AdEvent.Type.DURATION_CHANGE]: this.#onAdEvent('addurationchange'),
            [google.ima.AdEvent.Type.IMPRESSION]: this.#onAdEvent('adimpression'),
            [google.ima.AdEvent.Type.INTERACTION]: this.#onAdEvent('adinteraction'),
            [google.ima.AdEvent.Type.LOADED]: this.#onAdLoaded,
            [google.ima.AdEvent.Type.PAUSED]: this.#onAdPause,
            [google.ima.AdEvent.Type.RESUMED]: this.#onAdEvent('adresume'),
            [google.ima.AdEvent.Type.SKIPPED]: this.#onAdEvent('adskipped'),
            [google.ima.AdEvent.Type.STARTED]: this.#onAdEvent('adstart'),
            [google.ima.AdEvent.Type.VOLUME_CHANGED]: this.#onSimpleAdEvent('advolumechange'),
            [google.ima.AdEvent.Type.VOLUME_MUTED]: this.#onSimpleAdEvent('advolumemuted'),
        };
        Object.keys(this.#adManagerEvents).forEach((key) => {
            const handler = this.#adManagerEvents[key];
            if (handler) {
                this.#adsManager?.addEventListener(key, this.#adManagerEvents[key]);
            }
        });
    }
    #deregisterAdsManagerListeners() {
        if (!this.#adManagerEvents) {
            return;
        }
        Object.keys(this.#adManagerEvents).forEach((key) => {
            const handler = this.#adManagerEvents[key];
            if (handler) {
                this.#adsManager?.removeEventListener(key, this.#adManagerEvents[key]);
            }
        });
    }
    #emitAdEvent = (eventType, event) => {
        const ad = event.getAd();
        const adData = event.getAdData();
        const data = {
            adType: 'client',
            requestId: this.#adRequestId,
            adPosition: ad?.getAdPodInfo()?.getAdPosition(),
            contentType: ad?.getContentType(),
            creativeId: ad?.getCreativeId(),
            eventType,
            height: ad?.getHeight() || ad?.getVastMediaHeight(),
            id: ad?.getAdId(),
            totalAds: ad?.getAdPodInfo()?.getTotalAds(),
            width: ad?.getWidth() || ad?.getVastMediaWidth(),
            bitrate: adData?.vastMediaBitrate,
            destinationUrl: adData?.clickThroughUrl,
            companionAds: this.#getCompanionAds(ad),
        };
        this.emit(eventType, data);
    };
    #emitAdError = (errorData, requestId = this.#adRequestId) => {
        this.#didAdFail = true;
        this.emit('aderror', {
            isAdError: true,
            requestId,
            ...errorData,
        });
    };
    #onAdEvent = (eventType) => (event) => {
        this.#emitAdEvent(eventType, event);
    };
    #onSimpleAdEvent = (eventType) => () => {
        this.emit(eventType, { eventType });
    };
    #onAdError = () => (event) => {
        const error = event.getError();
        this.#emitAdError({
            message: error.getMessage(),
            name: error.name,
            errorCode: error.getErrorCode(),
            vastErrorCode: error.getVastErrorCode(),
            shouldAbortAdBreak: !this.#didAdLoad,
            type: error.getType(),
            innerError: error.getInnerError(),
        });
    };
    #onAdPause = (event) => {
        this.#isPaused = true;
        this.#emitAdEvent('adpause', event);
    };
    #onAdProgress = (event) => {
        const { duration, currentTime } = event?.getAdData() || {};
        this.#currentTime = currentTime;
        this.emit('adprogress', {
            adType: 'client',
            eventType: 'adprogress',
            duration: duration,
            currentTime: currentTime,
        });
    };
    #onAllAdsCompleted = (event) => {
        this.#emitAdEvent('alladscomplete', event);
    };
    #onAdLoaded = (event) => {
        const ad = event.getAd();
        const adData = event.getAdData();
        this.#didAdLoad = true;
        if (!ad) {
            this.#emitAdEvent('adloaded', event);
            return;
        }
        this.#duration = adData?.duration;
        const width = ad.getWidth() || DEFAULT_VIDEO_WIDTH;
        const height = ad.getHeight() || DEFAULT_VIDEO_HEIGHT;
        const isVideoAd = ad.getContentType()?.includes('video') || !!adData?.vpaid;
        debug('Google IMA ad loaded', {
            adPosition: ad.getAdPodInfo()?.getAdPosition(),
            contentType: ad.getContentType(),
            height,
            isLinear: ad.isLinear(),
            isVpaid: Boolean(adData?.vpaid),
            totalAds: ad.getAdPodInfo()?.getTotalAds(),
            width,
        });
        this.#containerElement?.toggleAttribute('data-video', isVideoAd);
        if (isVideoAd) {
            this.#containerElement?.setAttribute('data-url', adData?.clickThroughUrl);
            const containerWidth = this.#containerElement?.clientWidth || width;
            const containerHeight = this.#containerElement?.clientHeight || height;
            this.resizeVideo(containerWidth, containerHeight);
        }
        if (!ad?.isLinear()) {
            debug('Google IMA ad is non-linear; waiting for an explicit content resume event');
        }
        this.#emitAdEvent('adloaded', event);
    };
    #onContentPauseRequested = () => {
        this.emit('contentpauserequested');
    };
    #onContentResumeRequested = (event) => {
        if (this.#options.allowContentlessVmap && !this.#didAdLoad && !this.#didAdFail) {
            debug('Google IMA content resume requested before ad load; signaling content complete for a contentless VMAP request', {
                requestId: this.#adRequestId,
            });
            this.#adsLoader?.contentComplete();
            return;
        }
        this.#emitAdEvent('contentresumerequested', event);
    };
}
